import React from "react";
import { UserButton } from "@clerk/clerk-react";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "components/shadcn/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "components/shadcn/ui/tooltip";
import { useSidebar } from "./useSidebar";

interface SidebarProps {
  user: any;
}

const Sidebar: React.FC<SidebarProps> = ({ user }) => {
  const { collapsed, setCollapsed, selected, navItems, handleNavigation } =
    useSidebar(user);

  return (
    <TooltipProvider>
      <div
        className={`h-screen flex flex-col bg-white border-r transition-all duration-300 ${
          collapsed ? "w-[70px]" : "w-60"
        }`}
      >
        <div className="relative flex items-center justify-center px-3 py-4 border-b bg-white shrink-0">
          <img
            src={collapsed ? "/assets/bcLetterLogo.png" : "/assets/bcLogo.png"}
            alt="Logo"
            className="h-8 object-contain transition-all duration-300"
          />

          <div
            className="absolute top-1/2 -right-5 transform -translate-y-1/2 z-50 transition-all duration-300 hover:-right-6"
            style={{ pointerEvents: "auto" }}
          >
            <Button
              variant="secondary"
              size="icon"
              className="rounded-full bg-white border shadow-md hover:bg-gray-50 w-8 h-8 flex items-center justify-center"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? (
                <ChevronsRight className="w-4 h-4 text-gray-700" />
              ) : (
                <ChevronsLeft className="w-4 h-4 text-gray-700" />
              )}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-3 p-3 w-full">
            {navItems
              .filter((item) => item.visible)
              .map((item) => {
                const active = selected === item.key;
                const button = (
                  <Button
                    key={item.key}
                    variant={active ? "primary" : "secondary"}
                    className={`w-full flex items-center transition-all duration-300 ${
                      collapsed ? "justify-center" : "!justify-start"
                    } ${active ? "bg-[#FFF1F3] font-semibold" : "bg-gray-100"}`}
                    onClick={() =>
                      handleNavigation(item.path ?? "", item.label ?? "")
                    }
                  >
                    {item.icon}
                    {!collapsed && <span className="ml-3">{item.label}</span>}
                  </Button>
                );

                return collapsed ? (
                  <Tooltip key={item.key}>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                ) : (
                  button
                );
              })}
          </div>
        </div>

        <div className="border-t p-4 flex items-center justify-center bg-white shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: "w-8 h-8",
                    userButtonPopoverCustomItemButton:
                      "font-normal text-gray-800 font-sans",
                  },
                }}
                showName={!collapsed}
              />
            </TooltipTrigger>
            <TooltipContent side="right">Manage Profile</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Sidebar;
